import React, { useState } from "react";
import "./ReviewerEvaluationForm.css";

const ReviewerEvaluationForm = ({
  onSubmit,
  defaultReviewerName = "",
  defaultReviewerEmail = "",
  defaultApplicantName = "",
  defaultApplicantEmail = "",
  reviewFormId = "",
}) => {
  const [formData, setFormData] = useState({
    applicantName: defaultApplicantName,
    applicantEmail: defaultApplicantEmail,
    innovativenessScore: "",
    marketOpportunityScore: "",
    teamExpertiseScore: "",
    iprScore: "",
    businessModelScore: "",
    societalImpact: "",
    nameOfReviewer: defaultReviewerName,
    emailOfReviewer: defaultReviewerEmail,
    reviewFormId: reviewFormId,
    remark: "",
  });

  const handleScoreSelect = (field, score) => {
    setFormData((prev) => ({
      ...prev,
      [field]: score,
    }));
  };

  const scoreGroups = {
    innovativenessScore: {
      0: [
        "Completely replicates existing solutions.",
        "No novelty in idea, design, or approach.",
      ],
      2: [
        "Very similar to many existing solutions.",
        "No clear originality; feels derivative.",
      ],
      4: [
        "Small improvement over existing products/processes.",
        "Differentiation is marginal or easily replicable.",
      ],
      6: [
        "Creative combination or adaptation of existing elements.",
        "Not fully new but adds a useful feature or unique angle.",
      ],
      8: [
        "Clear novelty or unconventional approach.",
        "Stands out in structure, logic, or application.",
      ],
      10: [
        "First-of-its-kind or near-first.",
        "Introduces new science, logic, or breakthrough model.",
      ],
    },

    marketOpportunityScore: {
      0: [
        "No identifiable market, demand, or use-case.",
        "Problem not validated.",
      ],
      2: [
        "Small or unclear market.",
        "Weak demand signals; low scalability.",
      ],
      4: [
        "Serves a niche market with some demand.",
        "Impact possible but limited or unproven.",
      ],
      6: [
        "Clear target market with measurable demand.",
        "Reasonable scalability and adoption potential.",
      ],
      8: [
        "Large, growing, or underserved market.",
        "Evidence of strong demand and significant impact potential.",
      ],
      10: [
        "TAM is substantial; solution can transform markets or industries.",
        "High scalability with potential for global adoption.",
      ],
    },

    teamExpertiseScore: {
      0: [
        "Team lacks required technical or business skills.",
        "No clarity in roles or responsibilities.",
      ],
      2: [
        "Limited experience in the problem domain.",
        "Skill gaps present; execution ability questionable.",
      ],
      4: [
        "Team has some relevant experience but lacks depth.",
        "Requires significant external support.",
      ],
      6: [
        "Team possesses relevant domain knowledge and functional skills.",
        "Capable of executing with moderate guidance.",
      ],
      8: [
        "Highly experienced and well-balanced team.",
        "Clear track record and strong execution ability.",
      ],
      10: [
        "Deep expertise, proven success, and industry recognition.",
        "Capable of executing high-complexity projects at scale.",
      ],
    },

    iprScore: {
      0: ["No protectable element; fully generic.", "No IP awareness or strategy."],
      2: [
        "Claims innovation but not protectable.",
        "No filings, documentation, or strategy.",
      ],
      4: [
        "Some novelty that may be protectable.",
        "Initial documentation exists but no filings.",
      ],
      6: [
        "Distinct features with good potential for patent, copyright, or trademark.",
        "Preliminary IP work or provisional filing.",
      ],
      8: [
        "Clear patentable elements or unique algorithms/technology.",
        "Active filing process or granted trademarks/copyrights.",
      ],
      10: [
        "Granted patents / multiple filings.",
        "Strong defensibility; high entry barrier for competitors.",
      ],
    },

    businessModelScore: {
      0: ["No clear revenue model, pricing, or customer understanding."],
      2: [
        "Incomplete model; assumptions unclear or non-viable.",
        "No go-to-market strategy.",
      ],
      4: [
        "Revenue model exists but lacks validation.",
        "Early thoughts on GTM but no concrete plan.",
      ],
      6: [
        "Clear and practical revenue model.",
        "Defined target customers and workable GTM strategy.",
      ],
      8: [
        "Well-validated model with early traction.",
        "Clear acquisition channels and growth plan.",
      ],
      10: [
        "Highly scalable model with strong unit economics.",
        "Strategic clarity, traction, partnerships, and sustainable advantage.",
      ],
    },
  };

  const sections = [
    { label: "1. Innovativeness", field: "innovativenessScore" },
    { label: "2. Market Opportunity", field: "marketOpportunityScore" },
    { label: "3. Team & Expertise", field: "teamExpertiseScore" },
    {
      label: "4. IPR Inclusivity (Patentability & IP Strength)",
      field: "iprScore",
    },
    { label: "5. Business Model & Market Strategy", field: "businessModelScore" },
  ];

  const scoreLabels = {
    innovativenessScore: {
      0: "0 – No Innovation",
      2: "2 – Highly Generic",
      4: "4 – Minor Differentiation",
      6: "6 – Moderate Innovation",
      8: "8 – Strong Originality",
      10: "10 – Disruptive Innovation",
    },

    marketOpportunityScore: {
      0: "0 – No Market Fit",
      2: "2 – Very Limited Opportunity",
      4: "4 – Moderate Opportunity",
      6: "6 – Good Market Potential",
      8: "8 – Strong Market Opportunity",
      10: "10 – Massive Market Impact",
    },

    teamExpertiseScore: {
      0: "0 – No Relevant Skills",
      2: "2 – Weak Capability",
      4: "4 – Basic Competence",
      6: "6 – Good Expertise",
      8: "8 – Strong, Complementary Team",
      10: "10 – Exceptional Team",
    },

    iprScore: {
      0: "0 – No IP Consideration",
      2: "2 – Weak IP Awareness",
      4: "4 – Basic IP Potential",
      6: "6 – Moderate IP Strength",
      8: "8 – Strong IP Readiness",
      10: "10 – Robust IP Portfolio",
    },

    businessModelScore: {
      0: "0 – No Business Model",
      2: "2 – Weak or Unrealistic Model",
      4: "4 – Basic Model",
      6: "6 – Solid Model & Strategy",
      8: "8 – Strong Business Strategy",
      10: "10 – Exceptional, Scalable Model",
    },
  };

  const handleSubmit = (e) => {
    e.preventDefault();
      console.log("🔥 FORM SUBMISSION DATA (Frontend):", formData);
    if (onSubmit) onSubmit(formData);
  };

  return (
    <div className="page-bg-reviewerevaluationform">
      <div className="wrapper-reviewerevaluationform">
        <form className="form-reviewerevaluationform" onSubmit={handleSubmit}>

          {/* Applicant Info */}
          <div className="section-reviewerevaluationform">
            <div className="applicant-flex-reviewerevaluationform">
              <div className="applicant-field-reviewerevaluationform">
                <label className="label-reviewerevaluationform">Applicant Name</label>
                <input
                  className="input-reviewerevaluationform"
                  type="text"
                  value={formData.applicantName}
                  readOnly
                />
              </div>

              <div className="applicant-field-reviewerevaluationform">
                <label className="label-reviewerevaluationform">Applicant Email</label>
                <input
                  className="input-reviewerevaluationform"
                  type="email"
                  value={formData.applicantEmail}
                  readOnly
                />
              </div>
            </div>
          </div>

          {/* Reviewer Info */}
          <div className="section-reviewerevaluationform">
            <div className="applicant-flex-reviewerevaluationform">
              <div className="applicant-field-reviewerevaluationform">
                <label className="label-reviewerevaluationform">Reviewer Name</label>
                <input
                  className="input-reviewerevaluationform"
                  type="text"
                  value={formData.nameOfReviewer}
                  readOnly
                />
              </div>

              <div className="applicant-field-reviewerevaluationform">
                <label className="label-reviewerevaluationform">Reviewer Email</label>
                <input
                  className="input-reviewerevaluationform"
                  type="email"
                  value={formData.emailOfReviewer}
                  readOnly
                />
              </div>
            </div>

            <input type="hidden" value={formData.reviewFormId} readOnly />
          </div>

          {/* Score Sections */}
          {sections.map((sec, idx) => (
            <div key={idx} className="score-section-reviewerevaluationform">
              {/* <h3 className="score-title-reviewerevaluationform">{sec.label}</h3> */}
              <h3 className="score-title-reviewerevaluationform">
  <span className="score-heading-number-circle">
    {sec.label.split(".")[0]}
  </span>
  <span className="score-heading-text">
    {sec.label.substring(sec.label.indexOf(".") + 1).trim()}
  </span>
</h3>
              <div className="score-grid-reviewerevaluationform">
                {[[0, 2], [4, 6], [8, 10]].map((pair, p) => (
                  <React.Fragment key={p}>
                    {pair.map((score) => (
                      // <div key={score} className="score-block-reviewerevaluationform">
                     // {/*  START CHANGE: Make Score Cards Clickable  */}
<div
  key={score}
  className={`score-block-reviewerevaluationform score-card-animated ${
    formData[sec.field] === score ? "active-score-card-reviewer" : ""
  }`}
  onClick={() => handleScoreSelect(sec.field, score)}
  style={{ cursor: "pointer", position: "relative" }}
>
  {/* Tick Icon */}
  {formData[sec.field] === score && (
    <span className="score-card-tick">✓</span>
  )}
{/*  END CHANGE  */}

                        <p className="score-label-reviewerevaluationform">
                          {scoreLabels[sec.field][score]}
                        </p>

                        <ul className="score-list-reviewerevaluationform">
                          {scoreGroups[sec.field][score].map((txt, i) => (
                            <li key={i}>{txt}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>

              {/* <div className="score-buttons-container-reviewerevaluationform">
                {[0, 2, 4, 6, 8, 10].map((score) => (
                  <button
                    type="button"
                    key={score}
                    className={`score-btn-reviewerevaluationform ${
                      formData[sec.field] === score
                        ? "active-score-btn-reviewerevaluationform"
                        : ""
                    }`}
                    onClick={() => handleScoreSelect(sec.field, score)}
                  >
                    {score}
                  </button>
                ))}
              </div> */}
            </div>
          ))}

          {/* Societal Impact */}
          <div className="section-reviewerevaluationform">
            {/* <label className="score-title-reviewerevaluationform">
              6. Has societal impact?
            </label> */}
<h3 className="score-title-reviewerevaluationform">
  <span className="score-heading-number-circle">6</span>
  <span className="score-heading-text">Has societal impact?</span>
</h3>

            <div className="radio-group-reviewerevaluationform">
              <label>
                <input
                  type="radio"
                  value="Yes"
                  checked={formData.societalImpact === "Yes"}
                  onChange={(e) =>
                    setFormData({ ...formData, societalImpact: e.target.value })
                  }
                />
                Yes
              </label>

              <label>
                <input
                  type="radio"
                  value="No"
                  checked={formData.societalImpact === "No"}
                  onChange={(e) =>
                    setFormData({ ...formData, societalImpact: e.target.value })
                  }
                />
                No
              </label>
            </div>
          </div>

          {/* Remarks */}
          <div className="section-reviewerevaluationform remarks-section-reviewerevaluationform">
            {/* <label className="score-title-reviewerevaluationform">
              7. Reviewer Remarks
            </label> */}
<h3 className="score-title-reviewerevaluationform">
  <span className="score-heading-number-circle">7</span>
  <span className="score-heading-text">Reviewer Remarks</span>
</h3>
            <textarea
              className="input-reviewerevaluationform"
              rows="4"
              placeholder="Enter your remark or additional comments here..."
              value={formData.remark}
              onChange={(e) =>
                setFormData({ ...formData, remark: e.target.value })
              }
            />
          </div>

          {/* <button className="submit1-btn1-reviewerevaluationform" type="submit">
            Submit Evaluation
          </button> */}
          <button
  type="submit"
  style={{
    width: "150px",
    display: "block",
    margin: "25px auto 0",
    background: "#2B3674",
    color: "white",
    padding: "8px 0",
    borderRadius: "5px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    cursor: "pointer",
    textAlign: "center"
  }}
  onMouseOver={(e) => (e.currentTarget.style.background = "#1f285a")}
  onMouseOut={(e) => (e.currentTarget.style.background = "#2B3674")}
>
  Submit Evaluation
</button>

        </form>
      </div>
    </div>
  );
};

export default ReviewerEvaluationForm;
